/* Copyright (c) 2017 Backpack Health LLC */

import React from 'react';
import { FormattedMessage } from 'react-intl';

import TextAreaCardView from './components/TextAreaCardView';
import { GreenWhiteToggleSwitch } from '../../components/ToggleSwitch';
import FlexRow from '../../components/FlexRow';
import { RelativeDate } from '../../localization/LocalizedDate';

import Comment, { CommentCollection } from '../../../models/Comment';
import RouteNames from '../../appAuthorized/RouteNames';
import { useHealthEntryForActiveProfile, useHealthEntryAndValueForActiveProfile } from '../../hooks/useHealthEntry';
import { useNavigate } from '../../hooks/useNavigate';
import { useExistingTrackableForActiveProfile } from '../../hooks/useTrackable';

function useHEComment(healthEntryId) {
  const [{ isLoaded }, heActions] = useHealthEntryForActiveProfile(healthEntryId);

  return {
    getInstance: () => {
      return heActions.instantiateHealthEntry();
    },
    submit: (comment) => {
      const updatedHEData = heActions.instantiateHealthEntry().data();
      const commentCollection = new CommentCollection(updatedHEData.comments);
      commentCollection.update(comment);
      updatedHEData.comments = commentCollection.data;
      return heActions.apiUpdate(updatedHEData, true);
    },
    remove: (comment) => {
      const updatedHEData = heActions.instantiateHealthEntry().data();
      const commentCollection = new CommentCollection(updatedHEData.comments);
      commentCollection.remove(comment);
      updatedHEData.comments = commentCollection.data;
      return heActions.apiUpdate(updatedHEData);
    },
    isLoaded,
  };
}

function useHEVComment(healthEntryId, healthEntryValueId) {
  const [{ isLoaded }, hevActions] = useHealthEntryAndValueForActiveProfile(healthEntryId, healthEntryValueId);
  return {
    getInstance: () => {
      return hevActions.instantiateHealthEntryValue();
    },
    submit: (comment) => {
      const heValueData = hevActions.instantiateHealthEntryValue().data();
      const commentCollection = new CommentCollection(heValueData.comments);
      commentCollection.update(comment);
      heValueData.comments = commentCollection.data;
      return hevActions.apiUpdate(heValueData);
    },
    remove: (comment) => {
      const heValue = hevActions.instantiateHealthEntryValue();
      const heValueData = heValue.data();
      const commentCollection = new CommentCollection(heValueData.comments);
      commentCollection.remove(comment);
      heValueData.comments = commentCollection.data;
      return hevActions.apiUpdate(heValueData);
    },
    isLoaded,
  };
}

interface Props {
  isCreating: boolean;
  goBack(): void;
  onSubmit(): void;
  onDelete(): void;
  onChange(c): void;
  comment: any;
}
const OccurrenceComment: React.FC<Props> = (props) => {
  const { isCreating, goBack, onSubmit, onDelete, onChange, comment } = props;

  const [isDirty, setIsDirty] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const value = comment.content;

  return (
    <TextAreaCardView
      value={value}
      placeholderPhrase={''}
      hasInitialFocus={isCreating}
      isCreating={isCreating}
      onSubmit={() => {
        if (!isDirty) { goBack(); return; }
        setIsSubmitting(true);
        onSubmit();
      }}
      onChange={(v) => {
        const c = new Comment(comment);
        c.setContent(v);
        onChange(c.data);
        setIsDirty(true);
      }}
      isValid={true}
      isSubmitting={isSubmitting}
      onCancel={goBack}
      onDelete={onDelete}
      footerLeftContent={(
        <div className="font-caption-1 font-color-gsMedDark">
          <RelativeDate
            date={comment.updated_at}
            precision="P1D"
            dateKind="medium"
          />
        </div>
      )}
      additionalCardContent={(
        <div>
          <FlexRow style={{ justifyContent: 'flex-start' }}>
            <GreenWhiteToggleSwitch
              value={comment.include_on_share_card}
              choices={[
                { label: '', name: 'not_included', value: false },
                { label: '', name: 'included', value: true },
              ]}
              onChange={() => {
                const c = new Comment(comment);
                c.toggleIncludeOnShareCard();
                onChange(c.data);
                setIsDirty(true);
              }}
              isOff={!comment.include_on_share_card}
            />

            <div className="font-footnote font-color-gsDark" style={{ marginLeft: 8 }}>
              <FormattedMessage id="sharecardInclude.comment.button.label" />
            </div>
          </FlexRow>
          <div className="font-footnote font-color-gsMedDark" style={{ marginTop: 14 }}>
            <FormattedMessage id="sharecardInclude.comment.message" />
          </div>
        </div>
      )}
    />
  );
};

interface ViewProps {
  match: any;
}
export const HealthEntryComment: React.FC<ViewProps> = (props) => {
  const isCreating = props.match.params.commentId === 'create';
  const healthEntryId = Number(props.match.params.healthEntryId);
  const healthEntryValueId = Number(props.match.params.valueId);
  const commentId = isCreating ? null : Number(props.match.params.commentId);

  const actions = healthEntryValueId ? useHEVComment(healthEntryId, healthEntryValueId) : useHEComment(healthEntryId);
  const [, navActions] = useNavigate();
  const [comment, setComment] = React.useState(Comment.empty());

  const isLoaded = isCreating || actions.isLoaded;

  const goBack = () => {
    navActions.goBack(RouteNames.healthEntry(healthEntryId));
  };

  React.useEffect(() => {
    if (!isCreating && healthEntryId) {
      const heInstance = actions.getInstance();
      const comments = heInstance.comments.getAll();
      const commentCollection = new CommentCollection(comments);
      const c = commentCollection.get(commentId);
      setComment(c);
    }
  }, [isLoaded]);

  return (
    <OccurrenceComment
      comment={comment}
      isCreating={isCreating}
      goBack={goBack}
      onSubmit={() => {
        actions.submit(comment).then(goBack);
      }}
      onDelete={() => {
        actions.remove(comment).then(goBack);
      }}
      onChange={(c) => {
        setComment(c);
      }}
    />
  );
};

export const TrackableComment: React.FC<ViewProps> = (props) => {
  const isCreating = props.match.params.commentId === 'create';
  const trackableId = Number(props.match.params.trackableId);
  const commentId = isCreating ? null : Number(props.match.params.commentId);

  const [{ trackable, ...trackableData }, trackableActions] = useExistingTrackableForActiveProfile(trackableId);
  const [, navActions] = useNavigate();
  const [comment, setComment] = React.useState(Comment.empty());

  const isLoaded = isCreating || trackableData.isLoaded;

  const goBack = () => {
    navActions.goBack(RouteNames.trackableObservationList(trackableId));
  };

  React.useEffect(() => {
    if (isLoaded && !isCreating && trackableId) {
      const commentCollection = new CommentCollection(trackable.comments);
      const c = commentCollection.get(commentId);
      setComment(c);
    }
  }, [isLoaded]);

  return (
    <OccurrenceComment
      comment={comment}
      isCreating={isCreating}
      goBack={goBack}
      onSubmit={() => {
        const trackableInstance = trackableActions.instantiate();
        trackableInstance.updateComment(comment);
        trackableActions.apiUpdate(trackableInstance.data).then(goBack);
      }}
      onDelete={() => {
        const trackableInstance = trackableActions.instantiate();
        trackableInstance.removeComment(comment);
        trackableActions.apiUpdate(trackableInstance.data).then(goBack);
      }}
      onChange={(c) => {
        setComment(c);
      }}
    />
  );
};
